import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { ColumnDef } from '@tanstack/react-table';
import DataTable from '../../common/DataTable';
import PersonaBadge from '../../common/PersonaBadge';

interface Props {
  nodes: any[];
}

interface ChurnRow {
  path: string;
  persona: string;
  hydration_count: number;
  last_hydrated?: string;
  status: string;
}

const columns: ColumnDef<ChurnRow, any>[] = [
  { accessorKey: 'path', header: 'Path', cell: ({ getValue }) => <span className="font-mono text-xs">{getValue() as string}</span> },
  {
    accessorKey: 'persona',
    header: 'Persona',
    cell: ({ getValue }) => {
      const name = getValue() as string;
      return name && name !== 'unassigned'
        ? <PersonaBadge personaId="" personaName={name} size="xs" clickable={false} />
        : <span className="text-gray-500 text-xs">unassigned</span>;
    },
  },
  {
    accessorKey: 'hydration_count',
    header: 'Hydrations',
    size: 90,
    cell: ({ getValue }) => {
      const count = getValue() as number;
      return (
        <span className={count >= 5 ? 'text-red-400 font-bold' : count >= 3 ? 'text-amber-400 font-medium' : ''}>
          {count}
        </span>
      );
    },
  },
  { accessorKey: 'status', header: 'Status', size: 80 },
  {
    accessorKey: 'last_hydrated',
    header: 'Last Hydrated',
    cell: ({ getValue }) => {
      const val = getValue() as string | undefined;
      return val ? new Date(val).toLocaleDateString() : '\u2014';
    },
  },
];

const QualityChurn: React.FC<Props> = ({ nodes }) => {
  const { darkMode } = useTheme();

  const churnData: ChurnRow[] = nodes
    .filter(n => (n.metadata?.hydration_count || 0) >= 2)
    .map(n => ({
      path: n.path,
      persona: n.assigned_persona_id || 'unassigned',
      hydration_count: n.metadata?.hydration_count || 0,
      last_hydrated: n.metadata?.last_hydration,
      status: n.status,
    }))
    .sort((a, b) => b.hydration_count - a.hydration_count);

  const churnCount = churnData.filter(n => n.hydration_count >= 3).length;

  return (
    <div className="space-y-6">
      {churnCount > 0 && (
        <div className={`p-3 rounded-lg border ${darkMode ? 'bg-red-900/10 border-red-700/30' : 'bg-red-50 border-red-200'}`}>
          <p className="text-sm text-red-400">{churnCount} node{churnCount !== 1 ? 's' : ''} with 3+ re-hydrations — potential churn</p>
        </div>
      )}

      <div className={`rounded-xl border p-5 ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h3 className={`text-sm font-semibold mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Re-Hydration Leaderboard</h3>
        {churnData.length === 0 ? (
          <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No re-hydrated nodes. All nodes have been hydrated at most once.</p>
        ) : (
          <DataTable columns={columns} data={churnData} pageSize={15} />
        )}
      </div>
    </div>
  );
};

export default QualityChurn;
